// Logement type (spécification métier du 22/10/2026, partie C.2) — calcul PUR, partagé entre le serveur et l'interface.
// Quantité d'un poste = Σ par variante (quantité du logement type × logements réels de cette variante) + Σ écarts + parties communes.
// Décimal exact (decimal.js), jamais de flottant. Aucune valeur par défaut : une quantité absente du gabarit vaut « rien », jamais une estimation.
import { Decimal } from 'decimal.js';

const D = Decimal.clone({ precision: 40 });

export interface LigneProgramme { cle: string; libelle: string; quantite: string; unite: string }
export interface TypologieProgramme {
  id: string; typologie: string; variante: string | null; attendu: number; source: string;
  /** Gabarit : clé de ligne du devis → quantité par logement. */
  quantites: Record<string, string>;
}
export interface EcartProgramme { id: string; cle: string; delta: string; motif: string; auteur?: string | null; date?: string | null }
export interface LogementProgramme { id: string; numero: string; typologieId: string | null; miroir: boolean; ecarts: EcartProgramme[] }
export interface PartieCommuneProgramme {
  id: string; cle: string; libelle: string; nature: 'saisie' | 'verticale';
  /** null = verticale non calculable (hauteur d'étage manquante…) : voir `manque`. */
  quantite: string | null; manque?: string | null;
}
export interface EntreeProgramme {
  lignes: LigneProgramme[]; typologies: TypologieProgramme[]; logements: LogementProgramme[]; partiesCommunes: PartieCommuneProgramme[];
}

export interface DetailLigne {
  cle: string; libelle: string; unite: string;
  /** Quantité de la ligne au devis ; null = ligne absente de la révision du devis du chantier. */
  devis: string | null;
  typologies: { typologieId: string; libelle: string; logements: number; parLogement: string; total: string }[];
  ecarts: { logementId: string; numero: string; delta: string; motif: string }[];
  totalEcarts: string;
  partiesCommunes: { id: string; libelle: string; quantite: string | null }[];
  totalPartiesCommunes: string;
  somme: string;
  /** somme − devis ; null si la ligne n'est pas au devis. */
  ecart: string | null;
  juste: boolean;
}
export interface Controle { niveau: 'bloquant' | 'alerte'; code: string; message: string; ou: string }
export interface ResultatProgramme {
  lignes: DetailLigne[];
  typologies: { id: string; libelle: string; attendu: number; rattaches: number; source: string }[];
  controles: Controle[];
  bloque: boolean;
}

export const libelleTypologie = (t: { typologie: string; variante: string | null }) => (t.variante ? `${t.typologie} ${t.variante}` : t.typologie);

/** Nombre au format français, sans zéros inutiles (2,5 ; 12 ; 0,3333). */
export const nombreFr = (x: Decimal.Value) => new D(x).toFixed().replace('.', ',');
/** Écart signé avec un vrai signe moins (+2 ; −2 ; 0). */
export const signeFr = (x: Decimal.Value) => { const d = new D(x); return d.gt(0) ? `+${nombreFr(d)}` : d.lt(0) ? `−${nombreFr(d.abs())}` : '0'; };

/** Quantités d'UN logement réel, ligne par ligne : gabarit de sa variante + écarts actifs. Peut être négatif (contrôle bloquant). */
export function quantitesLogement(l: LogementProgramme, typologies: TypologieProgramme[]): Map<string, Decimal> {
  const r = new Map<string, Decimal>();
  const t = l.typologieId ? typologies.find((x) => x.id === l.typologieId) : undefined;
  for (const [cle, q] of Object.entries(t?.quantites ?? {})) r.set(cle, new D(q));
  for (const e of l.ecarts) r.set(e.cle, (r.get(e.cle) ?? new D(0)).plus(e.delta));
  return r;
}

/** Contrôles du C.2 : nombre de logements par variante, somme par poste = devis, logements sans typologie, etc. */
export function calculerProgramme(e: EntreeProgramme): ResultatProgramme {
  const controles: Controle[] = [];
  const parCle = new Map(e.lignes.map((l) => [l.cle, l]));
  const cles: string[] = [];
  const voir = (cle: string) => { if (!cles.includes(cle)) cles.push(cle); };
  for (const t of e.typologies) Object.keys(t.quantites).forEach(voir);
  for (const l of e.logements) l.ecarts.forEach((x) => voir(x.cle));
  for (const p of e.partiesCommunes) voir(p.cle);
  // Ordre des lignes : celui du devis, puis les lignes inconnues.
  cles.sort((a, b) => idx(a) - idx(b));
  function idx(c: string) { const i = e.lignes.findIndex((l) => l.cle === c); return i < 0 ? Number.MAX_SAFE_INTEGER : i; }

  const typologies = e.typologies.map((t) => ({ id: t.id, libelle: libelleTypologie(t), attendu: t.attendu, source: t.source,
    rattaches: e.logements.filter((l) => l.typologieId === t.id).length }));

  if (!e.typologies.length) controles.push({ niveau: 'bloquant', code: 'programme_vide', ou: 'typologies',
    message: 'Aucun logement type : créez au moins une typologie et son gabarit avant de valider.' });
  for (const t of typologies) {
    if (t.rattaches !== t.attendu) controles.push({ niveau: 'bloquant', code: 'nombre_logements', ou: `typologie:${t.id}`,
      message: `${t.attendu} ${t.libelle} attendu${t.attendu > 1 ? 's' : ''} au programme (${t.source}), ${t.rattaches} rattaché${t.rattaches > 1 ? 's' : ''}.` });
    const gabarit = e.typologies.find((x) => x.id === t.id)!;
    if (!Object.keys(gabarit.quantites).length) controles.push({ niveau: 'alerte', code: 'gabarit_vide', ou: `typologie:${t.id}`,
      message: `Logement type ${t.libelle} : aucun poste dans le gabarit.` });
  }
  for (const l of e.logements) {
    if (!l.typologieId) controles.push({ niveau: 'alerte', code: 'logement_sans_typologie', ou: `logement:${l.id}`,
      message: `Logement ${l.numero} sans typologie : ${l.ecarts.length ? 'seuls ses écarts comptent' : 'il ne reçoit aucune quantité'}.` });
    for (const [cle, q] of quantitesLogement(l, e.typologies)) if (q.lt(0)) controles.push({ niveau: 'bloquant', code: 'quantite_negative', ou: `logement:${l.id}`,
      message: `Logement ${l.numero}, « ${parCle.get(cle)?.libelle ?? 'ligne absente du devis'} » : ${signeFr(q)} ${parCle.get(cle)?.unite ?? ''} après écarts. Une quantité ne peut pas être négative.`.replace(' .', '.') });
  }
  for (const p of e.partiesCommunes) if (p.quantite === null) controles.push({ niveau: 'bloquant', code: 'verticale_incomplete', ou: `partie:${p.id}`,
    message: `Parties communes « ${p.libelle} » : ${p.manque ?? 'quantité à calculer'}. Aucune hauteur n'est supposée.` });

  const lignes: DetailLigne[] = cles.map((cle) => {
    const ligne = parCle.get(cle);
    const parTypo = e.typologies.filter((t) => t.quantites[cle] !== undefined).map((t) => {
      const n = e.logements.filter((l) => l.typologieId === t.id).length;
      return { typologieId: t.id, libelle: libelleTypologie(t), logements: n, parLogement: new D(t.quantites[cle]).toFixed(), total: new D(t.quantites[cle]).mul(n).toFixed() };
    });
    const ecarts = e.logements.flatMap((l) => l.ecarts.filter((x) => x.cle === cle).map((x) => ({ logementId: l.id, numero: l.numero, delta: new D(x.delta).toFixed(), motif: x.motif })));
    const pcs = e.partiesCommunes.filter((p) => p.cle === cle).map((p) => ({ id: p.id, libelle: p.libelle, quantite: p.quantite }));
    const totalEcarts = ecarts.reduce((s, x) => s.plus(x.delta), new D(0));
    const totalPc = pcs.reduce((s, x) => s.plus(x.quantite ?? 0), new D(0));
    const somme = parTypo.reduce((s, x) => s.plus(x.total), new D(0)).plus(totalEcarts).plus(totalPc);
    const devis = ligne ? new D(ligne.quantite) : null;
    const ecart = devis ? somme.minus(devis) : null;
    const unite = ligne?.unite ?? '';
    const libelle = ligne?.libelle ?? 'Ligne absente du devis du chantier';
    if (!ligne) controles.push({ niveau: 'bloquant', code: 'ligne_absente', ou: `ligne:${cle}`,
      message: `Une ligne utilisée par la répartition n'existe pas dans la révision du devis du chantier : retirez-la des logements types, des écarts et des parties communes.` });
    else if (!ecart!.eq(0)) {
      const detail = [
        ...parTypo.map((t) => `${t.logements} × ${t.libelle} × ${nombreFr(t.parLogement)} = ${nombreFr(t.total)}`),
        ...(ecarts.length ? [`écarts ${signeFr(totalEcarts)} (${ecarts.map((x) => `logement ${x.numero} : ${signeFr(x.delta)}`).join(', ')})`] : []),
        ...(pcs.length ? [`parties communes ${nombreFr(totalPc)}`] : []),
      ].join(' ; ');
      controles.push({ niveau: 'bloquant', code: 'somme_devis', ou: `ligne:${cle}`,
        message: `« ${libelle} » : ${nombreFr(somme)} ${unite} répartis pour ${nombreFr(devis!)} ${unite} au devis, écart ${signeFr(ecart!)} ${unite}. Détail : ${detail || 'rien de réparti'}.` });
    }
    return { cle, libelle, unite, devis: devis?.toFixed() ?? null, typologies: parTypo, ecarts, totalEcarts: totalEcarts.toFixed(),
      partiesCommunes: pcs, totalPartiesCommunes: totalPc.toFixed(), somme: somme.toFixed(), ecart: ecart?.toFixed() ?? null, juste: !!ecart?.eq(0) };
  });
  return { lignes, typologies, controles, bloque: controles.some((c) => c.niveau === 'bloquant') };
}
